import React from "react";

import {
  Dialog,
  DialogContent,
  DialogOverlay,
} from "../../components/ui/dialog";
import { selectsCrawlerPreferences } from "../constants";
import { useIntegrationModalHandlers } from "../hooks/useIntegrationModalHandlers";
import { useIntegrationModalState } from "../hooks/useIntegrationModalState";
import { useIntegrationURL } from "../hooks/useIntegrationURL";
import { useModalStepDetermination } from "../hooks/useModalStepDetermination";

import { IntegrationModalContent } from "./IntegrationModalContent";

import type { IntegrationScope } from "../constants";
import type { Integration } from "../types/integrations";
import type { IntegrationModalAppearance, ModalStep } from "../types/modal";

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: Integration | null;
  integrationScope?: IntegrationScope;
  // Pins the opening step. Use to connect an additional account, which would otherwise
  // resolve to the manage step because one is already connected.
  startStep?: ModalStep;
  appearance?: IntegrationModalAppearance;
}

export function IntegrationModal({
  isOpen,
  onClose,
  integration,
  integrationScope,
  startStep,
  appearance = "default",
}: IntegrationModalProps) {
  // Modal state management
  const {
    crawlerPreferenceError,
    fromCrawlerPreference,
    selectedAccountId,
    internalIsOpen,
    actualIsOpen,
    showNonOAuthForm,
    oauthCallbackState,
    setCrawlerPreferenceError,
    setFromCrawlerPreference,
    setSelectedAccountId,
    setInternalIsOpen,
    setShowNonOAuthForm,
    setOauthCallbackState,
    resetState,
  } = useIntegrationModalState({ isOpen, integration });

  // URL handling for OAuth callbacks and step determination
  const { checkOAuthCallback, shouldOpenFromURL, setModalURL } =
    useIntegrationURL();

  // Capture OAuth callback state when modal first opens to persist it
  React.useEffect(() => {
    if (actualIsOpen && integration && !oauthCallbackState) {
      const oauthInfo = checkOAuthCallback();
      if (oauthInfo.isOAuthCallback) {
        setOauthCallbackState({
          isOAuthCallback: true,
          success: oauthInfo.success,
          reason: oauthInfo.reason,
        });
      }
    }
  }, [
    actualIsOpen,
    integration,
    oauthCallbackState,
    checkOAuthCallback,
    setOauthCallbackState,
  ]);

  // Check if this integration should be opened from URL
  React.useEffect(() => {
    if (integration) {
      const shouldOpen = shouldOpenFromURL(integration.label);

      if (shouldOpen && !internalIsOpen) {
        setInternalIsOpen(true);
      } else if (!shouldOpen && internalIsOpen && !isOpen) {
        setInternalIsOpen(false);
      }
    }
  }, [
    integration,
    isOpen,
    internalIsOpen,
    shouldOpenFromURL,
    setInternalIsOpen,
  ]);

  // Handle browser back/forward navigation
  React.useEffect(() => {
    if (!integration) return;

    const handlePopState = () => {
      const shouldOpen = shouldOpenFromURL(integration.label);
      if (shouldOpen && !actualIsOpen) {
        setInternalIsOpen(true);
      } else if (!shouldOpen && actualIsOpen && !isOpen) {
        setInternalIsOpen(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [integration, actualIsOpen, isOpen, shouldOpenFromURL, setInternalIsOpen]);

  // Determine initial step from URL parameters or stored OAuth state
  const initialStep = React.useMemo((): ModalStep => {
    if (!actualIsOpen || !integration) return "authenticate";

    // Use stored OAuth state if available, otherwise check URL
    const oauthInfo = oauthCallbackState || checkOAuthCallback();

    if (oauthInfo.isOAuthCallback) {
      // For OAuth callbacks, determine next step based on success status and crawler preferences
      if (oauthInfo.success) {
        const needsCrawlerPreferences = selectsCrawlerPreferences(
          integration.label,
          integrationScope,
        );

        if (needsCrawlerPreferences) {
          // Reset the flag for OAuth callback - this is Flow A (first time connection)
          setFromCrawlerPreference(false);
          return "crawler_preference"; // Go to crawler preference step for successful OAuth with crawler preferences
        } else {
          // Reset the flag for non-crawler preference integrations
          setFromCrawlerPreference(false);
          return "result"; // Go directly to result for successful OAuth without crawler preferences
        }
      } else {
        // For OAuth failures, always go to result step to show error
        setFromCrawlerPreference(false);
        return "result"; // Go to result step to show failure message
      }
    }

    return "authenticate";
  }, [
    actualIsOpen,
    integration,
    oauthCallbackState,
    checkOAuthCallback,
    setFromCrawlerPreference,
    integrationScope,
  ]);

  // Auto-determine the correct step based on integration connection status or URL
  const {
    currentStep,
    isLoading: isStepLoading,
    setStep,
  } = useModalStepDetermination({
    integration,
    isOpen: actualIsOpen,
    initialStep,
    integrationScope,
    startStep,
  });

  // Event handlers
  const {
    handleConnect,
    handleCrawlerPreferenceNext,
    handleCrawlerPreferenceError,
    handleManage,
    handleClose,
    handleSetAccountId,
  } = useIntegrationModalHandlers({
    integration,
    onClose: () => {
      resetState();
      onClose();
    },
    setStep,
    setCrawlerPreferenceError,
    setFromCrawlerPreference,
    setSelectedAccountId,
    setShowNonOAuthForm,
    integrationScope,
  });

  // Set URL when modal opens, preserving OAuth params
  React.useEffect(() => {
    if (actualIsOpen && integration) {
      setModalURL(integration.label);
    }
  }, [actualIsOpen, integration, setModalURL]);

  // Determine success state for result step
  const isResultSuccess = React.useMemo(() => {
    if (currentStep === "result") {
      // If there's a crawler preference error, mark as failure
      if (crawlerPreferenceError) {
        return false;
      }

      // Use stored OAuth state if available, otherwise check URL
      const oauthInfo = oauthCallbackState || checkOAuthCallback();
      if (oauthInfo.isOAuthCallback) {
        return oauthInfo.success;
      }
      // Default to true for non-OAuth result steps
      return true;
    }
    return true;
  }, [
    currentStep,
    oauthCallbackState,
    checkOAuthCallback,
    crawlerPreferenceError,
  ]);

  if (!integration) return null;

  return (
    <Dialog open={actualIsOpen} onOpenChange={handleClose}>
      <DialogOverlay className="bg-black/30 dark:bg-black/50" />
      <DialogContent
        className="p-0 border-0 bg-transparent shadow-none max-w-none w-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Centralized modal styling - DRY principle */}
        <div
          className={
            appearance === "polished"
              ? "flex max-h-[calc(100dvh-4rem)] w-[600px] max-w-[calc(100dvw-2rem)] flex-col overflow-hidden rounded-[14px] bg-card p-7 shadow-xl"
              : "bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 w-[580px] max-w-[calc(100dvw-2rem)] max-h-[calc(100dvh-4rem)] flex flex-col overflow-hidden"
          }
        >
          <IntegrationModalContent
            integration={integration}
            integrationScope={integrationScope}
            currentStep={currentStep}
            isStepLoading={isStepLoading}
            selectedAccountId={selectedAccountId}
            isResultSuccess={isResultSuccess}
            errorMessage={crawlerPreferenceError}
            fromCrawlerPreference={fromCrawlerPreference}
            showNonOAuthForm={showNonOAuthForm}
            onConnect={handleConnect}
            onCrawlerPreferenceNext={handleCrawlerPreferenceNext}
            onCrawlerPreferenceError={handleCrawlerPreferenceError}
            onManage={handleManage}
            onClose={handleClose}
            onSetAccountId={handleSetAccountId}
            appearance={appearance}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
